/**
 * Default payment terms for direct-import bills.
 *
 * Stored on billSettings.directBillDueTerms as:
 *   { type: 'none' }                              - leave the due date blank
 *   { type: 'days', days: 14 }                    - N days after the invoice date
 *   { type: 'followingMonth', dayOfMonth: 20 }    - the Nth of the month after the invoice date
 * plus `override: true` to replace a due date read off the supplier's bill.
 */

export const DUE_TERM_PRESETS = [
  { key: 'none', label: 'No default', terms: { type: 'none' } },
  { key: 'days7', label: '7 days after invoice date', terms: { type: 'days', days: 7 } },
  { key: 'days14', label: '14 days after invoice date', terms: { type: 'days', days: 14 } },
  { key: 'dom20', label: '20th of the following month', terms: { type: 'followingMonth', dayOfMonth: 20 } },
  { key: 'customDays', label: 'Custom: days after invoice date', terms: { type: 'days', days: 30 } },
  { key: 'customDom', label: 'Custom: day of the following month', terms: { type: 'followingMonth', dayOfMonth: 1 } }
];

/** Clamp and tidy stored terms; anything unrecognised becomes 'none'. */
export function normalizeDueTerms(terms) {
  const override = terms?.override === true;
  if (terms?.type === 'days') {
    const days = Math.round(Number(terms.days));
    if (Number.isFinite(days) && days >= 0 && days <= 365) return { type: 'days', days, override };
  }
  if (terms?.type === 'followingMonth') {
    const dayOfMonth = Math.round(Number(terms.dayOfMonth));
    if (Number.isFinite(dayOfMonth) && dayOfMonth >= 1 && dayOfMonth <= 31) {
      return { type: 'followingMonth', dayOfMonth, override };
    }
  }
  return { type: 'none', override: false };
}

/** Which preset a stored terms object corresponds to (for the settings dropdown). */
export function presetKeyForTerms(terms) {
  const t = normalizeDueTerms(terms);
  if (t.type === 'days') return t.days === 7 ? 'days7' : t.days === 14 ? 'days14' : 'customDays';
  if (t.type === 'followingMonth') return t.dayOfMonth === 20 ? 'dom20' : 'customDom';
  return 'none';
}

/** Short human label, e.g. "14 days" or "20th of following month". */
export function describeDueTerms(terms) {
  const t = normalizeDueTerms(terms);
  if (t.type === 'days') return `${t.days} day${t.days === 1 ? '' : 's'} after invoice date`;
  if (t.type === 'followingMonth') return `${ordinal(t.dayOfMonth)} of the following month`;
  return 'No default';
}

function ordinal(n) {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
}

function parseYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

function ymd(y, m, d) {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * Due date (YYYY-MM-DD) from an invoice date (YYYY-MM-DD) and terms, or null.
 * Works on calendar parts in UTC so a timezone can never shift the day.
 * "Following month" clamps to the month's last day (31st in Feb → 28th/29th).
 */
export function computeDueDate(issueDate, terms) {
  const t = normalizeDueTerms(terms);
  const p = parseYmd(issueDate);
  if (!p || t.type === 'none') return null;

  if (t.type === 'days') {
    const d = new Date(Date.UTC(p.y, p.m - 1, p.d + t.days));
    return ymd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
  }

  const nextMonthIndex = p.m; // 0-based index of the following month
  const y = p.y + Math.floor(nextMonthIndex / 12);
  const m0 = nextMonthIndex % 12;
  const lastDay = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
  return ymd(y, m0 + 1, Math.min(t.dayOfMonth, lastDay));
}

/**
 * The due date a bill should show: the supplier's own date wins unless the
 * terms say override. Returns { dueDate, defaulted } where `defaulted` means
 * the date came from the terms, not the bill.
 */
export function resolveBillDueDate({ issueDate, dueDate }, terms) {
  const t = normalizeDueTerms(terms);
  if (dueDate && !t.override) return { dueDate, defaulted: false };
  const computed = computeDueDate(issueDate, t);
  if (computed) return { dueDate: computed, defaulted: computed !== dueDate };
  return { dueDate: dueDate || '', defaulted: false };
}
